// weatherLabels.ts
// ─────────────────────────────────────────────────────────────────────────────
// TV-broadcast-style place labels: uppercase, bold, white text with a heavy
// black halo, centered on the place point, sized so cities read at a glance
// over the weather raster. Plus zoom-based density control so the map isn't a
// wall of town names.
//
// VISIBILITY MODEL — we trust the tile's per-feature `min_zoom`.
// The Protomaps `places` source-layer bakes a `min_zoom` onto every place: the
// zoom at which that specific place is meant to start showing (a big city might
// be min_zoom 4, a hamlet min_zoom 13). The tile authors already did the work
// of ranking places sensibly. So a place is ELIGIBLE once the map zoom reaches
// its `min_zoom`. We do NOT re-derive visibility from population curves — an
// earlier version did, and its modeled population thresholds (e.g. ~2300 at
// z11) sat far above the real rural villages (Shirley ME = 229), so they never
// showed. min_zoom matches the actual data.
//
// DENSITY — once eligible, places compete for space via MapLibre's collision
// system. A zoom-ramped `text-padding` (wide when zoomed out, tight when zoomed
// in) controls how crowded labels may get, and a population-derived
// `symbol-sort-key` makes the MOST POPULOUS place win each contested spot — so
// the labels that drop as you zoom out are always the smallest ones.
//
// RURAL BOOST (0–100) — a single control that pulls smaller places in EARLIER
// for rural users: it lets a place show up to ~`boost` worth of zoom levels
// before its baked-in min_zoom (so min_zoom 12 places can appear at z11/z10).
// At 0 it's exactly the tile's schedule. It only affects small places
// (min_zoom ≥ TOWN_MIN_ZOOM); big cities/towns are unaffected.
//
//   • STATE labels hide at/below z3.5 (handled on the places_region layer).
//   • CENTERING: Protomaps' default uses text-variable-anchor (labels float
//     around the point to dodge collisions), which leaves them off-center.
//     We delete it and pin labels centered on the point for the broadcast look.
//
// NOTE ON SOURCE-LAYER vs STYLE-LAYER: in this Protomaps flavor every place
// (cities, towns, villages, regions, countries) lives in ONE source-layer
// called `places`, distinguished by a `kind` attribute. The STYLE layers still
// use the split ids ("places_locality", "places_region", …) and each carries a
// `kind` filter. PLACE_LAYER_IDS below matches those style-layer ids. Because
// the locality layer's own filter encodes `kind == locality`, when we rewrite
// its filter we MUST re-include that guard (see buildLocalityFilter), or the
// layer would try to draw regions/countries too.
//
// We MUTATE the existing Protomaps place layers rather than adding a parallel
// layer, so there's exactly one set of labels and it survives setStyle on
// every flavor change (this runs inside buildDarkStyle).

export const PLACE_LAYER_IDS = new Set([
  "places_locality",   // cities / towns / villages (all kind=locality)
  "places_region",     // states / provinces (the "MA", "ME" labels)
  "places_subplace",   // neighborhoods
  "places_country",
]);

// ─── Density spacing ─────────────────────────────────────────────────────────
// How much breathing room (px) each label claims for collision purposes, as a
// function of zoom. WIDE when zoomed out (sparse, well-spaced) easing to TIGHT
// when zoomed in (dense). This — NOT population — controls spacing. Higher px =
// bigger exclusion box = fewer labels survive overlap culling. Label count
// scales roughly with the inverse square of padding, so to thin the visible
// count by ~30% you raise padding by ~20%.
const PADDING_STOPS: [number, number][] = [
  [6, 76],
  [8, 40],
  [10, 24],
  [11, 12],
  [12, 5],
];

/** Zoom-interpolated `text-padding` expression (see PADDING_STOPS). Fed
 *  straight into the locality layer's layout so the GPU does the per-zoom
 *  math — no JS runs on pan/zoom. */
function paddingExpr(): any[] {
  return ["interpolate", ["linear"], ["zoom"],
    ...PADDING_STOPS.flatMap(([z, px]) => [z, px]),
  ];
}

// ─── Rural boost ───────────────────────────────────────────────────────────
// Population floor (from the tile's own `min_zoom` semantics) separating the
// "small places" that rural boost helps from the big cities/towns it leaves
// alone. Places whose min_zoom is at least TOWN_MIN_ZOOM are considered small
// enough that a rural user benefits from seeing them earlier.
const TOWN_MIN_ZOOM = 9;

/** Clamp the single rural-boost control to 0–100. */
export function clampRuralBoost(v?: number | null): number {
  return Math.max(0, Math.min(100, Number.isFinite(v as number) ? (v as number) : 0));
}

export const DEFAULT_RURAL_BOOST = 0; // 0 = exactly the tile's min_zoom schedule.

// Max zoom-levels-earlier a fully-cranked rural boost (100) pulls small places
// in. boost 100 → small places appear up to this many zooms before their
// baked-in min_zoom. e.g. a min_zoom-12 village can surface at z9.5.
const RURAL_BOOST_MAX_DZ = 2.5;

// ─── Per-zoom label sizing ──────────────────────────────────────────────────
// Base text sizes for locality labels, BEFORE the user's label scale is
// applied. Larger at low zoom (readable when zoomed out), peaking ~z9, easing
// back DOWN past z11 so labels don't dominate when zoomed in close. Values are
// the DEFAULTS reduced 20% from the original ramp (14→11.2, 18→14.4, etc.).
const CITY_SIZE_STOPS: [number, number][] = [
  [3, 11.2],
  [5, 14.4],
  [7, 19.2],
  [9, 20],
  [11, 14.4],
  [13, 11.2],
  [16, 8.8],
];

/** Clamp a user label scale into the supported band (matches the slider). */
export function clampLabelScale(labelScale: number): number {
  return Math.max(0.5, Math.min(2, Number.isFinite(labelScale) ? labelScale : 1));
}

/** The scaled locality `text-size` zoom expression for a given label scale. */
function cityTextSizeExpr(scale: number): any[] {
  return ["interpolate", ["linear"], ["zoom"],
    ...CITY_SIZE_STOPS.flatMap(([z, px]) => [z, Math.round(px * scale * 10) / 10]),
  ];
}

/** Zoom at/below which US state/region labels hide. */
export const STATE_MIN_ZOOM = 3.5;

/** How much breathing room (px) each label claims for collision purposes. */
const LABEL_PADDING = 30; // legacy constant kept for any external import; live path uses paddingExpr().

/**
 * The population expression used for the collision SORT KEY (who wins a
 * contested spot). Prefers raw `population`; where absent it synthesizes a
 * stand-in from `population_rank` so rank-only places still sort sensibly
 * rather than collapsing to 0. This is ONLY used for sort priority now —
 * visibility is decided by min_zoom, not population.
 */
function popExpr(): any[] {
  const rankFallback: any[] = ["step", ["coalesce", ["get", "population_rank"], 0],
    100,
    5, 800,
    7, 2500,
    9, 8000,
    10, 20000,
    11, 60000,
    12, 150000,
    13, 500000,
    14, 1500000,
  ];
  return ["case",
    [">", ["coalesce", ["get", "population"], 0], 0], ["get", "population"],
    rankFallback,
  ];
}

/**
 * Scale a `text-size` value by `scale`, correctly and without producing an
 * illegal expression. MapLibre requires ["zoom"] to appear ONLY as the direct
 * top-level input to step/interpolate — so we must NOT wrap a zoom expression
 * in ["*", expr, scale] (that buries ["zoom"] one level down and MapLibre
 * rejects the whole text-size). Instead:
 *   • number              → multiply directly.
 *   • interpolate/step    → clone and multiply each OUTPUT value, leaving the
 *                           ["zoom"] input in place (legal).
 *   • anything else/none  → fall back to a sane scaled base.
 * Safe to call from a cached ORIGINAL size so repeated slider drags never
 * compound.
 */
function scaleTextSize(size: any, scale: number): any {
  const round1 = (n: number) => Math.round(n * 10) / 10;
  if (typeof size === "number") return round1(size * scale);
  if (Array.isArray(size) && (size[0] === "interpolate" || size[0] === "step")) {
    const out = size.slice();
    if (size[0] === "interpolate") {
      for (let i = 4; i < out.length; i += 2) {
        if (typeof out[i] === "number") out[i] = round1(out[i] * scale);
      }
    } else {
      if (typeof out[2] === "number") out[2] = round1(out[2] * scale);
      for (let i = 4; i < out.length; i += 2) {
        if (typeof out[i] === "number") out[i] = round1(out[i] * scale);
      }
    }
    return out;
  }
  return round1(13 * scale);
}

// Remembers each non-city place layer's ORIGINAL (scale-1) text-size the first
// time we touch it, so repeated slider moves always scale from the original
// rather than compounding off the last scaled value. Keyed by layer id.
const originalNonCitySizes = new Map<string, any>();

/**
 * Apply the user's label scale to the LIVE map, in place, without a setStyle.
 * Walks the current style's place layers and updates their `text-size` via
 * setLayoutProperty. City layers get the full scaled ramp (rebuilt fresh, never
 * compounding); other place layers are scaled from their cached original size.
 */
export function applyLabelScaleLive(map: any, labelScale: number): void {
  if (!map || !map.getStyle) return;
  const scale = clampLabelScale(labelScale);
  let style: any;
  try { style = map.getStyle(); } catch { return; }
  const layers = style?.layers ?? [];
  for (const lyr of layers) {
    const id: string = lyr?.id ?? "";
    if (!PLACE_LAYER_IDS.has(id)) continue;
    if (lyr.type !== "symbol") continue;
    try {
      if (id === "places_locality") {
        map.setLayoutProperty(id, "text-size", cityTextSizeExpr(scale));
      } else {
        if (!originalNonCitySizes.has(id)) {
          originalNonCitySizes.set(id, lyr.layout?.["text-size"]);
        }
        const base = originalNonCitySizes.get(id);
        map.setLayoutProperty(id, "text-size", scaleTextSize(base, scale));
      }
    } catch { /* layer may be missing mid-restyle; ignore */ }
  }
}

/**
 * Detect whether a filter is already a modern expression (vs a legacy filter).
 * A legacy filter's comparison operand is a bare string property name, whereas
 * an expression uses ["get", …]. We treat a filter as an expression if its
 * first comparison argument is itself an array.
 */
function isExpressionFilterLike(f: any): boolean {
  if (!Array.isArray(f) || f.length === 0) return true;
  const op = f[0];
  if (op === "let" || op === "var" || op === "case" || op === "coalesce" ||
      op === "match" || op === "step" || op === "interpolate") return true;
  if (["==", "!=", ">", ">=", "<", "<=", "in", "!in"].includes(op)) {
    return Array.isArray(f[1]);
  }
  if (["all", "any", "none"].includes(op)) {
    return f.slice(1).some((c: any) => isExpressionFilterLike(c));
  }
  return false;
}

/**
 * Convert a (possibly legacy) filter to a modern expression so it can be safely
 * AND-ed with other expressions. Handles the legacy comparison idioms used by
 * Protomaps' place layers; anything already an expression is returned
 * unchanged.
 */
function toExpressionFilter(f: any): any {
  if (f == null) return null;
  if (isExpressionFilterLike(f)) return f;
  const op = f[0];
  if (["==", "!=", ">", ">=", "<", "<="].includes(op)) {
    return [op, ["get", f[1]], f[2]];
  }
  if (op === "has") return ["has", f[1]];
  if (op === "!has") return ["!", ["has", f[1]]];
  if (op === "in") {
    return ["in", ["get", f[1]], ["literal", f.slice(2)]];
  }
  if (op === "!in") {
    return ["!", ["in", ["get", f[1]], ["literal", f.slice(2)]]];
  }
  if (["all", "any", "none"].includes(op)) {
    const kids = f.slice(1).map(toExpressionFilter);
    if (op === "all") return ["all", ...kids];
    if (op === "any") return ["any", ...kids];
    return ["!", ["any", ...kids]];
  }
  return f;
}

/**
 * Build the MapLibre filter for `places_locality`.
 *
 * A place shows when:
 *   (1) it's a locality (the `kind` guard — REQUIRED because every place kind
 *       shares the `places` source-layer; without this the layer would also try
 *       to draw regions/countries), AND
 *   (2) the current zoom has reached the place's baked-in `min_zoom`, OPTIONALLY
 *       pulled earlier for small places by the rural boost.
 *
 * No population thresholds here — visibility is the tile's min_zoom. Population
 * only feeds the collision sort key (see applyWeatherLabelStyle). Spacing is the
 * text-padding ramp.
 *
 * RURAL BOOST: for small places (min_zoom ≥ TOWN_MIN_ZOOM) we subtract up to
 * RURAL_BOOST_MAX_DZ from the effective min_zoom, so they surface earlier. Big
 * places (min_zoom < TOWN_MIN_ZOOM) are never shifted. Concretely, a place is
 * visible when:
 *     zoom + dz(place) >= min_zoom
 * where dz = (boost/100)*RURAL_BOOST_MAX_DZ if min_zoom ≥ TOWN_MIN_ZOOM else 0.
 *
 * Wrapped in `let` so the whole thing parses as an EXPRESSION filter: MapLibre's
 * legacy-filter validator throws on array operands (our coalesce/case blocks),
 * and `let` exists only in the expression engine, forcing expression parsing.
 */
export function buildLocalityFilter(ruralBoost: number = DEFAULT_RURAL_BOOST): any[] {
  const boost = clampRuralBoost(ruralBoost);
  const maxDz = (boost / 100) * RURAL_BOOST_MAX_DZ;

  // min_zoom may be absent on a feature; default it high (15) so an untagged
  // place only appears when zoomed in close, rather than everywhere.
  const minZoom: any[] = ["coalesce", ["to-number", ["get", "min_zoom"]], 15];

  return ["let", "mz", minZoom,
    ["all",
      // (1) kind guard — this layer draws only localities.
      ["==", ["get", "kind"], "locality"],
      // (2) zoom has reached (boost-shifted) min_zoom.
      //     dz applies only to small places (mz >= TOWN_MIN_ZOOM).
      [">=",
        ["+", ["zoom"],
          ["case", [">=", ["var", "mz"], TOWN_MIN_ZOOM], maxDz, 0],
        ],
        ["var", "mz"],
      ],
    ],
  ];
}

/**
 * Apply a new rural boost to the LIVE map in place, without a setStyle.
 * Updates the locality layer's filter only.
 */
export function applyRuralBoostLive(map: any, ruralBoost: number): void {
  if (!map || !map.getStyle) return;
  let style: any;
  try { style = map.getStyle(); } catch { return; }
  const layers = style?.layers ?? [];
  const hasLocality = layers.some((l: any) => l?.id === "places_locality");
  if (!hasLocality) return;
  try {
    map.setFilter("places_locality", buildLocalityFilter(ruralBoost));
  } catch { /* layer may be missing mid-restyle; ignore */ }
}

/**
 * Apply the TV-broadcast place-label styling at build time (inside
 * buildDarkStyle). Bakes label scale, density padding, the min_zoom-based
 * locality filter (with rural boost), and the state-label z3.5 cutoff.
 *
 * @param styleLayers  The Protomaps layer list to mutate.
 * @param labelScale   User-controlled size multiplier (1 = default). Scales
 *                     locality label sizes and the region/country base size.
 * @param ruralBoost   0–100. Folded into the locality filter (pulls small
 *                     places in earlier) and baked so a basemap swap returns
 *                     with it applied.
 */
export function applyWeatherLabelStyle(
  styleLayers: any[],
  labelScale: number = 1,
  ruralBoost: number = DEFAULT_RURAL_BOOST,
): any[] {
  const scale = clampLabelScale(labelScale);
  const boost = clampRuralBoost(ruralBoost);

  return styleLayers.map((lyr) => {
    if (!PLACE_LAYER_IDS.has(lyr.id)) return lyr;

    const isCity = lyr.id === "places_locality";

    const layout = { ...(lyr.layout ?? {}) };
    layout["text-font"] = ["Noto Sans Bold"];

    if (isCity) {
      // Uppercase the place name.
      layout["text-field"] = ["upcase", ["coalesce", ["get", "name:en"], ["get", "name"]]];
      // Size ramp (see CITY_SIZE_STOPS), scaled by the user's labelScale.
      layout["text-size"] = cityTextSizeExpr(scale);
      // Zoom-ramped collision breathing room — controls sparse→dense.
      layout["text-padding"] = paddingExpr();
      // Most populous place wins each contested spot (negative pop = placed
      // first). This is why labels that drop when zooming out are the smallest.
      layout["symbol-sort-key"] = ["-", 0, popExpr()];
      layout["symbol-z-order"] = "source";

      // Center the label on the place point (delete the variable-anchor drift).
      delete layout["text-variable-anchor"];
      delete layout["text-radial-offset"];
      layout["text-anchor"] = "center";
      layout["text-offset"] = [0, 0];
    } else if (scale !== 1) {
      // Region / country / subplace: scale Protomaps' own sizing. scaleTextSize
      // keeps ["zoom"] a legal top-level input.
      layout["text-size"] = scaleTextSize(layout["text-size"], scale);
    }

    const paint = { ...(lyr.paint ?? {}) };
    paint["text-color"] = "#ffffff";
    paint["text-halo-color"] = "#000000";
    paint["text-halo-width"] = isCity ? 3 : 2;
    paint["text-halo-blur"] = 0.5;

    const result: any = { ...lyr, layout, paint };

    if (isCity) {
      // min_zoom-based visibility (+ rural boost), with kind guard. See
      // buildLocalityFilter.
      result.filter = buildLocalityFilter(boost);
    } else if (lyr.id === "places_region") {
      // State / province labels: hide at/below z3.5, show above. Preserve the
      // existing kind filter by AND-ing the zoom gate onto it (normalized to
      // expression form first, since Protomaps ships region as a legacy
      // ["==","kind","region"] filter).
      const zoomGate: any[] = [">=", ["zoom"], STATE_MIN_ZOOM];
      const existing = lyr.filter ? toExpressionFilter(lyr.filter) : null;
      result.filter = existing ? ["all", existing, zoomGate] : zoomGate;
    }

    return result;
  });
}